import React, {
  FunctionComponent,
  useState,
  SyntheticEvent,
  useRef,
} from 'react';
import withStyles from 'react-jss';
import { isEmpty } from 'ramda';
import { Checkbox, Grid, Header, Icon } from 'semantic-ui-react';
import { format } from 'd3-format';
import {
  ResponsiveContainer,
  ComposedChart,
  YAxis,
  Area,
  Tooltip,
  Line,
  XAxis,
} from 'recharts';

import Calendar from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/KeyMetricGraphs/components/Calendar';
import {
  LiftResults,
  tickFormatter,
  chartMargin,
  Data,
  DataType,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift.helpers';
import { Timeslice } from 'scenes/Report/Report.helper';
import DownloadWidget from 'components/DownloadWidget';
import { ClassProp } from 'helpers/types';
import { theme } from 'config';
import { getBaseStyles } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift.styles';

import CustomTooltip from 'components/CustomTooltip';

type Styles = ClassProp<typeof styles>;
type Props = {
  classes: Styles;
  data: Data;
};

const styles = getBaseStyles(
  {
    chart: { height: 400 },
    chartContent: {
      padding: '1rem !important',
    },
    header: {
      display: 'flex !important',
      alignItems: 'center !important',
      fontWeight: '500 !important',
    },
  },
  { checkboxColor: theme.violet },
);

const formatter = format('.0f');

const tooltipFields = (showControlGroup: boolean, type: DataType) => {
  const totalTransactions = {
    formatter,
    label: 'Total Transactions',
    value: 'count',
  };

  const controlGroup = {
    formatter,
    label: 'Control Group',
    value: 'scaledControlTransactionCount',
  };

  const liftPercent = {
    formatter: format('.1%'),
    label: 'Lift',
    value: ({ count, scaledControlTransactionCount }: LiftResults) =>
      (count - scaledControlTransactionCount) / scaledControlTransactionCount,
  };

  if (showControlGroup && type === 'control') {
    return [totalTransactions, controlGroup, liftPercent];
  }

  return [totalTransactions];
};

const Transactions: FunctionComponent<Props> = ({
  classes,
  data: { type, results, timeslice = Timeslice.DAILY },
}) => {
  const [showControlGroup, setShowControlGroup]: any = useState<boolean>(true);
  const contentRef = useRef(null);
  const isControlType = type === 'control';

  const toggleControlGroup = (
    _: SyntheticEvent<HTMLElement, Event>,
    { checked }: any,
  ) => {
    setShowControlGroup(checked);
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const renderHeader = (title: string, classes: Styles) => (
    <Header className={classes.header}>
      <Icon
        className={classes.headerIcon}
        name={isControlType ? 'chart line' : 'area chart'}
      />
      {title}

      {isControlType && (
        <Checkbox
          className={classes.headerCheckBox}
          label="Control Group"
          checked={showControlGroup}
          onChange={toggleControlGroup}
        />
      )}
    </Header>
  );

  const Chart = (isControlType ? Line : Area) as React.ElementType;

  return (
    <>
      <div ref={contentRef} className={classes.ref}>
        <Grid.Row centered className={classes.chartContent}>
          <Grid.Column
            width={16}
            columns={1}
            textAlign="center"
            verticalAlign="middle"
            className={classes.noPadding}
          >
            <Calendar
              timeslice={timeslice}
              liftResults={results as LiftResults[]}
              chartType={'transactions'}
            />

            <Grid.Column>
              {isEmpty(results) ? (
                <Header as="h3">
                  There is no data to display at this time.
                </Header>
              ) : (
                <div className={classes.chart}>
                  <ResponsiveContainer>
                    <ComposedChart data={results} margin={chartMargin}>
                      <YAxis tickFormatter={tickFormatter('.0f')} />
                      <XAxis tick={false} scale="point" />

                      <Tooltip
                        content={
                          <CustomTooltip
                            fields={tooltipFields(showControlGroup, type)}
                            xAxis={{
                              label: 'Day',
                              key: 'periodStart',
                            }}
                          />
                        }
                      />

                      {showControlGroup && (
                        <Area
                          type="linear"
                          dataKey="scaledControlTransactionCount"
                          fill={theme.violet}
                          stroke={theme.violet}
                        />
                      )}

                      <Chart
                        type="linear"
                        dataKey="count"
                        fill={theme.violet}
                        stroke={theme.violet}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Grid.Column>
          </Grid.Column>

          <Grid.Column width={16} className={classes.calendarLegend}>
            {renderHeader('Total Transactions', classes)}
          </Grid.Column>
        </Grid.Row>
      </div>

      <DownloadWidget
        nodeRef={contentRef}
        className={classes.download}
        options={['PNG', 'PDF']}
        imageName="Transactions"
      />
    </>
  );
};

export default withStyles(styles)(Transactions);
